"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GridTableApiImpl = exports.useGridTableApi = void 0;
const react_1 = require("react");
const TableState_1 = require("./utils/TableState");
const visitor_1 = require("./utils/visitor");
/**
 * Creates an `api` handle to drive a `GridTable`.
 *
 * ```
 * const api = useGridTableApi<Row>();
 * const count = useComputed(() => api.getSelectedRows().length, [api]);
 * ...
 * return <GridTable api={api} />
 * ```
 *
 * This is very similar to a `useRef`, except that the parent function has
 * immediate access to `api` and can use it for `useComputed`, instead of
 * having to wait for `ref.current` to be set after the child `GridTable`
 * has ran.
 */
function useGridTableApi() {
    return (0, react_1.useMemo)(() => new GridTableApiImpl(), []);
}
exports.useGridTableApi = useGridTableApi;
// Using `FooImpl`to keep the public GridTableApi definition separate.
class GridTableApiImpl {
    constructor() {
        // This is public to GridTable but not exported outside of Beam
        this.tableState = new TableState_1.TableState();
        this.virtuosoRef = { current: null };
    }
    /** Called once by the GridTable when it takes ownership of this api instance. */
    init(persistCollapse, virtuosoRef, rows) {
        this.tableState.loadCollapse(persistCollapse, rows);
        this.tableState.loadSelected(rows);
        this.virtuosoRef = virtuosoRef;
    }
    scrollToIndex(index) {
        this.virtuosoRef.current && this.virtuosoRef.current.scrollToIndex(index);
    }
    getSelectedRowIds(kind) {
        return this.getSelectedRows(kind).map((row) => row.id);
    }
    // The any is not great, but getting the overload to handle the optional kind is annoying
    getSelectedRows(kind) {
        const ids = this.tableState.selectedIds;
        const selected = [];
        (0, visitor_1.visit)(this.tableState.rows, (row) => {
            if (row.selectable !== false && ids.includes(row.id) && (!kind || row.kind === kind)) {
                selected.push(row);
            }
        });
        return selected;
    }
    clearSelections(id) {
        this.tableState.selectRow("header", false);
    }
    setActiveRowId(id) {
        this.tableState.activeRowId = id;
    }
    setActiveCellId(id) {
        this.tableState.activeCellId = id;
    }
    selectRow(id, selected = true) {
        this.tableState.selectRow(id, selected);
    }
    toggleCollapsedRow(id) {
        this.tableState.toggleCollapsed(id);
    }
    isCollapsedRow(id) {
        return this.tableState.isCollapsed(id);
    }
    setVisibleColumns(ids) {
        this.tableState.setVisibleColumns(ids);
    }
    getVisibleColumnIds() {
        return this.tableState.visibleColumnIds;
    }
}
exports.GridTableApiImpl = GridTableApiImpl;
